import { isReservedBundledAIAssistantSkillName, type AIAssistantClientSkill, type SkillSummary } from 'balus-common';

export type SkillCommandCandidate = {
  skill: SkillSummary;
  trigger: string;
};

type ChatInputResolution =
  | { type: 'message' }
  | { type: 'skillCommand'; skillName: string; trigger: string; args: string }
  | { type: 'invalidSkillCommand'; reason: 'unknown'; trigger: string }
  | {
      type: 'invalidSkillCommand';
      reason: 'ambiguous';
      trigger: string;
      candidates: SkillCommandCandidate[];
    };

function clientSkillToSummary(skill: AIAssistantClientSkill): SkillSummary {
  return {
    id: skill.id,
    name: skill.name,
    description: skill.description,
    triggers: skill.triggers,
    argumentHint: skill.argumentHint,
    whenToUse: skill.whenToUse,
    source: 'client',
  };
}

/**
 * backend の mergeSkillDefs と同じ同名Skillの優先順位で、チャット入力用の一覧を作る。
 */
export function mergeAIAssistantSkillSummaries(
  bundledSkills: readonly SkillSummary[],
  clientSkills: readonly AIAssistantClientSkill[]
): SkillSummary[] {
  const byName = new Map<string, SkillSummary>();

  for (const skill of bundledSkills) {
    byName.set(skill.name, skill);
  }

  for (const skill of clientSkills) {
    if (!skill.enabled || isReservedBundledAIAssistantSkillName(skill.name)) continue;
    byName.set(skill.name, clientSkillToSummary(skill));
  }

  return [...byName.values()];
}

export function buildSkillCommandCandidates(skills: readonly SkillSummary[]): SkillCommandCandidate[] {
  const candidates: SkillCommandCandidate[] = [];
  const seen = new Set<string>();

  for (const skill of skills) {
    for (const trigger of skill.triggers) {
      const key = `${skill.name}\0${trigger}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push({ skill, trigger });
    }
  }

  return candidates;
}

/**
 * チャット入力を通常メッセージ、Skill呼び出し、無効なSkill呼び出しのいずれかに分類する。
 */
export function resolveChatInput(input: string, candidates: readonly SkillCommandCandidate[]): ChatInputResolution {
  if (!input.startsWith('/')) return { type: 'message' };

  const firstWhitespaceIndex = input.search(/\s/);
  const trigger = firstWhitespaceIndex === -1 ? input : input.slice(0, firstWhitespaceIndex);
  const matchedCandidates = candidates.filter((candidate) => candidate.trigger === trigger);

  if (matchedCandidates.length === 0) {
    return { type: 'invalidSkillCommand', reason: 'unknown', trigger };
  }
  if (matchedCandidates.length > 1) {
    return {
      type: 'invalidSkillCommand',
      reason: 'ambiguous',
      trigger,
      candidates: matchedCandidates,
    };
  }

  const args = firstWhitespaceIndex === -1 ? '' : input.slice(firstWhitespaceIndex + 1).trim();
  return {
    type: 'skillCommand',
    skillName: matchedCandidates[0].skill.name,
    trigger,
    args,
  };
}
